import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import startCase from 'lodash/startCase';
import React from 'react';
import { useTranslation } from 'react-i18next';

import specialties from 'src/public/img/hand_with_heart.png';
import modalities from 'src/public/img/icon-modalities.png';
import licensedStatesIcon from 'src/public/img/licensed_states.png';
import location from 'src/public/img/location_mark.png';
import languages from 'src/public/img/speak.png';
import ttcBestMatchStar from 'src/public/img/time-to-care-best-match-star.svg';

import { CoachingVisitModalityType, CoachNode } from 'src/generated/webapp_gql';
import { ExpandableText } from 'src/modules/components/ExpandableText';
import {
    ProviderAvailability,
    ProviderAvailableThisWeekNextWeek,
    ProviderAvailableTodayTmrw,
    ProviderAvailableWithinAMonth,
    ProviderWithoutCalendar,
} from 'src/modules/providers/components/ProviderAvailability';
import { ProviderType } from 'src/modules/user/models/user';
import { Button } from 'src/shared/components/Button';
import { color, font, mediaQueries } from 'src/shared/styles/variables';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { useSelector } from 'react-redux';
import { SkeletonPlaceholder } from '@modernhealth/vital-components';

interface ProviderCardWrapperProps {
    isBestMatch?: boolean;
}

const ProviderCardWrapper = styled.li<ProviderCardWrapperProps>(props => ({
    width: '31%',
    minWidth: 380,
    margin: '0 12px',
    padding: props.isBestMatch ? '0 0 24px 0' : '20px 0 24px 0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderRadius: 12,
    marginBottom: 24,
    border: `1px solid ${color.catskillWhite}`,
    [mediaQueries.md]: {
        width: '100%',
    },
    [mediaQueries.sm]: {
        padding: 0,
        maxWidth: 380,
        margin: '0 0 20px',
    },
    textAlign: 'center',
}));

const ProviderInfoWrapper = styled.div({
    display: 'flex',
    padding: '0 24px',
    justifyContent: 'space-between',
    flexDirection: 'column',
    height: '100%',
    [mediaQueries.sm]: {
        padding: 20,
        maxWidth: 380,
    },
});

const ProviderAvatarWrapper = styled.div({
    position: 'relative',
});

const ProviderAvatar = styled.img({
    width: 60,
    height: 60,
    borderRadius: '50%',
    margin: '0 auto',
    display: 'block',
    border: 'none',
    objectFit: 'cover',
});

const ProviderAvatarPlaceholder = styled(ProviderAvatar.withComponent('div'))({
    backgroundColor: color.cornflowerBlue,
});

const ProviderInitials = styled.span(
    {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color.white,
    },
    font.heavy(font.size.extraLarge),
);

const ProviderName = styled.p(
    {
        textAlign: 'left',
        color: color.fiord,
    },
    font.black(font.size.large),
);

const providerIntroductionStyle = {
    textAlign: 'left',
    color: color.blueBayoux,
    lineHeight: 1.75,
    marginTop: 10,
    ...font.book(font.size.medium),
    display: 'inline',
} as const;

const ProviderTypeText = styled.p({
    textAlign: 'left',
    color: color.blueBayoux,
    marginBottom: 16,
    ...font.book(font.size.medium),
});

const InfoWrapper = styled.div({
    display: 'flex',
    backgroundColor: color.white,
    marginBottom: 16,
    borderRadius: 8,
});

const InfoRow = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: 16,
});

const InfoCol = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
});

const ProviderInfoRow = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
});

const ProviderInfoCol = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'center',
    paddingRight: '16px',
});

const ProviderSkeletonWrapper = styled.div({
    display: 'flex',
    width: '240px',
    alignItems: 'center',
});

const InfoText = styled.p({
    color: color.blueBayoux,
    flex: 1,
    textAlign: 'left',
    lineHeight: '20px',
    marginTop: 2,
    ...font.roman(font.size.base),
});

const RaceText = styled.p({
    backgroundColor: color.cornflowerBlueDark,
    color: color.white,
    textAlign: 'center',
    padding: '5px 10px',
    width: 'fit-content',
    marginBottom: 10,
    borderRadius: 50,
    ...font.heavy(font.size.base),
});

const BestMatchTabWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: color.dancingDolphinBlue,
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
});

const BestMatchStarImg = styled.img({
    height: 12,
    width: 12,
    marginRight: '4px',
    marginBottom: '4px',
});

const BestMatchText = styled.div({
    color: color.cornflowerBlueDark,
    ...font.heavy(font.size.small),
});

const IconImage = styled('img')({
    height: 12,
    width: 12,
    objectFit: 'contain',
    color: color.cornflowerBlueDark,
});

const ImageWrapper = styled.div({
    height: 24,
    width: 24,
    borderRadius: 12,
    marginRight: 12,
    backgroundColor: color.dancingDolphinBlue,
    justifyContent: 'center',
    alignItems: 'center',
});

export const titleCase = (text): string => (text ? startCase(text.toLowerCase()) : '');

export const formatProviderModalities = (therapistModalities): string => {
    const formattedModalities = (therapistModalities || []).map(modality => {
        if (
            modality === CoachingVisitModalityType.Phone ||
            modality === CoachingVisitModalityType.Video
        ) {
            return 'Teletherapy';
        } else if (modality === CoachingVisitModalityType.Unknown) {
            return '';
        }

        return titleCase(modality);
    });

    // Take out duplicates and empty string
    return formattedModalities
        .filter((modality, index) => formattedModalities.indexOf(modality) === index && !!modality)
        .join(', ');
};

export const formatProviderLocation = (country, city, state, zipCode, providerType): string => {
    const formattedCountry = titleCase(country);
    const providerLocation = [city, state, zipCode];

    if (providerType === ProviderType.COACH) {
        return formattedCountry;
    } else {
        return providerLocation.filter(info => !!info).join(', ');
    }
};

export enum ProviderCardTestID {
    Wrapper = 'ProviderCardTestID.Wrapper',
    MainButton = 'ProviderCardTestID.MainButton',
    LicensedStatesWrapper = 'ProviderCardTestID.LicensedStatesWrapper',
}

export enum ProviderAvailabilityTestID {
    Skeleton = 'ProviderAvailability.Skeleton',
    AvailabilityIcon = 'ProviderAvailability.AvailabilityIcon',
    AvailabilityText = 'ProviderAvailability.AvailabilityText',
}

export enum ProviderSpecialtyTypes {
    Other = 'Other',
}
interface ProviderCardProps {
    provider: CoachNode;
    loading: boolean;
    bestMatch: boolean;
    showLicensedStates?: boolean;
    onPress: () => void;
    isTimeToCareLoading: boolean;
    providerAvailability?:
        | 'AVAILABLE_TODAY'
        | 'AVAILABLE_TOMORROW'
        | 'AVAILABLE_THIS_WEEK'
        | 'AVAILABLE_NEXT_WEEK'
        | 'AVAILABLE_WITHIN_A_MONTH'
        | 'CONTACT_FOR_AVAILABILITY'
        | 'AVAILABILITY_UNKNOWN';
    disabled?: boolean;
}
export const ProviderCard: React.FC<ProviderCardProps> = ({
    onPress,
    loading,
    bestMatch,
    showLicensedStates,
    isTimeToCareLoading,
    providerAvailability,
    provider: {
        firstName,
        lastName,
        country,
        city,
        state,
        zipCode,
        introduction,
        avatar,
        languagePreferences,
        goalInterests,
        providerType,
        providerSpecialties,
        therapistModalities,
        licensedStates,
        preferencesMatched,
        psychiatristSubtype,
    },
    disabled,
}) => {
    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const { showDemographicsProjectWeb, pammMemberScheduling } = useFlags();
    const specialtiesToDisplay =
        providerType === ProviderType.COACH
            ? goalInterests
            : (providerSpecialties || []).filter(
                  speciality => speciality !== ProviderSpecialtyTypes.Other,
              );

    const formattedModalities =
        providerType === ProviderType.THERAPIST && formatProviderModalities(therapistModalities);

    const isProviderAvailable =
        providerAvailability &&
        (ProviderAvailableTodayTmrw[providerAvailability] === providerAvailability ||
            ProviderAvailableThisWeekNextWeek[providerAvailability] === providerAvailability ||
            ProviderAvailableWithinAMonth[providerAvailability] === providerAvailability ||
            ProviderWithoutCalendar[providerAvailability] === providerAvailability);

    const showTTC = !isTimeToCareLoading && isProviderAvailable;
    const showSkeleton = isTimeToCareLoading && !isProviderAvailable;
    const showButton =
        providerType !== ProviderType.PSYCHIATRIST ||
        (pammMemberScheduling && user.isCompanyPsychiatryEnabled);

    const getProviderDescription = () => {
        const providerTypeDescription: { [key: string]: string } = {
            [ProviderType.THERAPIST]: t('providers:provider_type.therapist'),
            [ProviderType.COACH]: t('providers:provider_type.coach'),
            [ProviderType.PSYCHIATRIST]: psychiatristSubtype || '',
        };

        return providerTypeDescription[providerType || ProviderType.COACH];
    };

    return (
        <ProviderCardWrapper data-testid={ProviderCardTestID.Wrapper} isBestMatch={bestMatch}>
            {bestMatch && (
                <BestMatchTabWrapper>
                    <BestMatchStarImg src={ttcBestMatchStar} />
                    <BestMatchText>
                        {t('providers:provider_card.best_match').toUpperCase()}
                    </BestMatchText>
                </BestMatchTabWrapper>
            )}
            <ProviderInfoWrapper>
                <div>
                    <InfoWrapper>
                        <InfoRow>
                            <ProviderInfoCol>
                                <ProviderAvatarWrapper>
                                    {avatar ? (
                                        <ProviderAvatar src={avatar} />
                                    ) : (
                                        <ProviderAvatarPlaceholder>
                                            <ProviderInitials>
                                                {firstName?.[0]}
                                                {lastName?.[0]}
                                            </ProviderInitials>
                                        </ProviderAvatarPlaceholder>
                                    )}
                                </ProviderAvatarWrapper>
                            </ProviderInfoCol>
                            <ProviderInfoCol>
                                <ProviderInfoRow>
                                    <ProviderName>{`${firstName} ${lastName}`}</ProviderName>
                                </ProviderInfoRow>
                                {showSkeleton && (
                                    <ProviderSkeletonWrapper
                                        data-testid={ProviderAvailabilityTestID.Skeleton}
                                    >
                                        <SkeletonPlaceholder
                                            width={160}
                                            height={20}
                                            marginTop={6}
                                            marginRight={8}
                                        />
                                    </ProviderSkeletonWrapper>
                                )}
                                {showTTC && (
                                    <ProviderAvailability
                                        providerAvailability={providerAvailability}
                                    />
                                )}
                                {providerType && (
                                    <ProviderInfoRow>
                                        <ProviderTypeText>
                                            {getProviderDescription()}
                                        </ProviderTypeText>
                                    </ProviderInfoRow>
                                )}
                            </ProviderInfoCol>
                        </InfoRow>
                    </InfoWrapper>

                    {showDemographicsProjectWeb &&
                        preferencesMatched?.map(data =>
                            data?.options?.map((race, i) => (
                                <RaceText key={`${i}-races-card`}>{race?.value}</RaceText>
                            )),
                        )}

                    <InfoWrapper>
                        <InfoCol>
                            <InfoRow>
                                <ImageWrapper>
                                    <IconImage src={specialties} />
                                </ImageWrapper>
                                <InfoText>{(specialtiesToDisplay || []).join(', ')}</InfoText>
                            </InfoRow>
                            <InfoRow>
                                <ImageWrapper>
                                    <IconImage src={languages} />
                                </ImageWrapper>
                                <InfoText>{(languagePreferences || []).join(', ')}</InfoText>
                            </InfoRow>
                            {providerType === ProviderType.THERAPIST && (
                                <InfoRow>
                                    <ImageWrapper>
                                        <IconImage src={modalities} />
                                    </ImageWrapper>
                                    <InfoText>{formattedModalities}</InfoText>
                                </InfoRow>
                            )}
                            <InfoRow>
                                <ImageWrapper>
                                    <IconImage src={location} />
                                </ImageWrapper>
                                <InfoText>
                                    {formatProviderLocation(
                                        country,
                                        city,
                                        state,
                                        zipCode,
                                        providerType,
                                    )}
                                </InfoText>
                            </InfoRow>
                            {showLicensedStates && licensedStates?.length ? (
                                <InfoRow data-testid={ProviderCardTestID.LicensedStatesWrapper}>
                                    <ImageWrapper>
                                        <IconImage src={licensedStatesIcon} />
                                    </ImageWrapper>
                                    <InfoText>
                                        {t('providers:provider_card.licensed', {
                                            license: licensedStates.join(', '),
                                        })}
                                    </InfoText>
                                </InfoRow>
                            ) : null}
                        </InfoCol>
                    </InfoWrapper>

                    <ExpandableText style={providerIntroductionStyle}>
                        {introduction}
                    </ExpandableText>
                </div>
                {showButton && (
                    <Button
                        onClick={onPress}
                        marginTop={25}
                        loading={loading}
                        testID={`${ProviderCardTestID.MainButton}`}
                        fullWidth
                        horizontalPadding="medium"
                        disabled={disabled}
                    >
                        {t('providers:provider_card.choose', { firstName })}
                    </Button>
                )}
            </ProviderInfoWrapper>
        </ProviderCardWrapper>
    );
};
